import * as React from "react";
import { AnalysisSetTestFixedComment, FixedComment, FvcAnalysisSet, FvcAnalysisSetTest, MepAnalysisSet, MipAnalysisSet, SnipAnalysisSet } from "webportalclient";
import { AnalysisSetFixedComment } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useFixedCommentsState } from "../../../Core/FixedCommentsContext";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { FixedCommentSetTypeEnum } from "../../../misc/api/FixedComments/FixedCommentSetTypes";
import { FixedCommentEntityTypeEnum } from "../../../misc/api/FixedComments/FixedCommentSetEntityTypes";
import { SelectableFixedComment } from "../../Shared/UseFixedComments";
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../../misc/api/WebPortalTokenClient';


export interface NextGenSessionFixedCommentState {
    isVisible: boolean;
    comments: SelectableFixedComment[];
}

export interface NextGenTestFixedCommentState {
    testId: string;
    isVisible: boolean;
    comments: SelectableFixedComment[];
}

export interface NextGenFixedCommentsState {
    isPageValidated: boolean;
    areAvailableFixedCommentsLoaded?: boolean;
    areFixedCommentsLoaded?: boolean;
    sessionFixedComments: NextGenSessionFixedCommentState;
    testFixedComments: NextGenTestFixedCommentState[];
}

const initialFixedCommentsState = {
    isPageValidated: true,
    areAvailableFixedCommentsLoaded: null,
    areFixedCommentsLoaded: null,
    sessionFixedComments: {
        isVisible: false,
        comments: [],
    },
    testFixedComments: [],
}

export enum NextGenFixedCommentsActionTypes {
    ToggleSessionFixedCommentChecked = "ToggleSessionFixedCommentChecked",
    ToggleTestFixedCommentChecked = "ToggleTestFixedCommentChecked",
    ToggleTestFixedCommentsVisibility = "ToggleTestFixedCommentsVisibility",
    ToggleSessionFixedCommentsVisibility = "ToggleSessionFixedCommentsVisibility",
    LoadSessionFixedComments = "LoadSessionFixedComments",
    LoadTestFixedComments = "LoadTestFixedComments",
    LoadAnalysisSetSelections = "LoadAnalysisSetSelections",
    PageValidated = "PageValidated",
    LoadResults = "LoadResults",
    LoadedBothAvailableFixedComments = "LoadedBothAvailableFixedComments",
    UpdateFixedComments = "UpdateFixedComments",
}

type Actions = LoadSessionFixedCommentsAction | LoadTestFixedCommentsAction | LoadedBothAvailableFixedCommentsAction | ToggleTestFixedCommentsVisibilityAction | ToggleSessionFixedCommentsVisibilityAction | ToggleTestFixedCommentCheckedAction | ToggleSessionFixedCommentCheckedAction | LoadAnalysisSetSelectionsAction | UpdateFixedCommentsAction;

export class LoadSessionFixedCommentsAction {
    type: NextGenFixedCommentsActionTypes.LoadSessionFixedComments;
    comments: FixedComment[];

    static create(comments: FixedComment[]): LoadSessionFixedCommentsAction {
        return {
            type: NextGenFixedCommentsActionTypes.LoadSessionFixedComments,
            comments: comments,
        };
    }
}

export class LoadTestFixedCommentsAction {
    type: NextGenFixedCommentsActionTypes.LoadTestFixedComments;
    comments: FixedComment[];

    static create(comments: FixedComment[]): LoadTestFixedCommentsAction {
        return {
            type: NextGenFixedCommentsActionTypes.LoadTestFixedComments,
            comments: comments,
        };
    }
}

export class LoadedBothAvailableFixedCommentsAction {
    type: NextGenFixedCommentsActionTypes.LoadedBothAvailableFixedComments;

    static create(): LoadedBothAvailableFixedCommentsAction {
        return {
            type: NextGenFixedCommentsActionTypes.LoadedBothAvailableFixedComments,
        }
    }
}

export class ToggleTestFixedCommentsVisibilityAction {
    type: NextGenFixedCommentsActionTypes.ToggleTestFixedCommentsVisibility;
    testId: string;

    static create(testId: string): ToggleTestFixedCommentsVisibilityAction {
        return {
            type: NextGenFixedCommentsActionTypes.ToggleTestFixedCommentsVisibility,
            testId: testId,
        };
    }
}

export class ToggleSessionFixedCommentCheckedAction {
    type: NextGenFixedCommentsActionTypes.ToggleSessionFixedCommentChecked;
    id: string;
    checked: boolean;

    static create(id: string, checked: boolean): ToggleSessionFixedCommentCheckedAction {
        return {
            type: NextGenFixedCommentsActionTypes.ToggleSessionFixedCommentChecked,
            id: id,
            checked: checked,
        };
    }
}

export class ToggleTestFixedCommentCheckedAction {
    type: NextGenFixedCommentsActionTypes.ToggleTestFixedCommentChecked;
    testId: string;
    commentId: string;
    checked: boolean;

    static create(testId: string, commentId: string, checked: boolean): ToggleTestFixedCommentCheckedAction {
        return {
            type: NextGenFixedCommentsActionTypes.ToggleTestFixedCommentChecked,
            testId: testId,
            commentId: commentId,
            checked: checked,
        };
    }
}

export class ToggleSessionFixedCommentsVisibilityAction {
    type: NextGenFixedCommentsActionTypes.ToggleSessionFixedCommentsVisibility;

    static create(): ToggleSessionFixedCommentsVisibilityAction {
        return {
            type: NextGenFixedCommentsActionTypes.ToggleSessionFixedCommentsVisibility,
        };
    }
}

export class LoadAnalysisSetSelectionsAction {
    type: NextGenFixedCommentsActionTypes.LoadAnalysisSetSelections;
    selectedSessionComments: AnalysisSetFixedComment[];
    selectedTestComments: FvcAnalysisSetTest[];

    static create(selectedComments: AnalysisSetFixedComment[], selectedTestComments: FvcAnalysisSetTest[]): LoadAnalysisSetSelectionsAction {
        return {
            type: NextGenFixedCommentsActionTypes.LoadAnalysisSetSelections,
            selectedSessionComments: selectedComments,
            selectedTestComments: selectedTestComments,
        };
    }
}

export class UpdateFixedCommentsAction {
    type: NextGenFixedCommentsActionTypes.UpdateFixedComments;
    comments: FixedComment[];
    updatedAnalysisSet: FvcAnalysisSet;

    static create(updatedAnalysisSet: FvcAnalysisSet, comments: FixedComment[]): UpdateFixedCommentsAction {
        return {
            type: NextGenFixedCommentsActionTypes.UpdateFixedComments,
            updatedAnalysisSet: updatedAnalysisSet,
            comments: comments
        };
    }
}

export const useNextGenFixedComments = (sessionTypeId: SessionTypes, context: FixedCommentSetTypeEnum, analysisSet: FvcAnalysisSet & MipAnalysisSet & MepAnalysisSet & SnipAnalysisSet): [NextGenFixedCommentsState, React.Dispatch<any>] => {
    const globalFixedCommentsState = useFixedCommentsState();
    const sessionFixedComments = globalFixedCommentsState.session.get(`${sessionTypeId}-${context}`);
    const testFixedComments = globalFixedCommentsState.test.get(`${sessionTypeId}-${context}`);
    const [overreadSelectableComments, setOverreadSelectableComments] = React.useState<SelectableFixedComment[]>()
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();

    React.useEffect(() => {
        const fetchData = async () => {
            const fixedCommentsResult = await client.client.getStudyFixedCommentSets(undefined, `studyId eq ${study.id} and sessionType eq '${sessionTypeId}'`, undefined, `fixedCommentSets($expand=comments)`);

            const filteredResults = fixedCommentsResult.value.filter(r => r.commentSetType === context).flatMap(fcset => {
                return fcset.fixedCommentSets.filter(f => f.entityType === FixedCommentEntityTypeEnum.Test);
            });

            const transformedComments = filteredResults.flatMap(fcSet =>
                fcSet.comments.map(comment => ({
                    ...comment,
                    selected: false,
                }))
            );

            setOverreadSelectableComments(transformedComments);
        }
        fetchData();
    }, []);

    const loadAnalysisSetSelections = (state: NextGenFixedCommentsState, action: LoadAnalysisSetSelectionsAction) => {
        const newState: NextGenFixedCommentsState = {
            ...state,
            sessionFixedComments: {
                ...state.sessionFixedComments,
                comments: state.sessionFixedComments.comments.map(c => {
                    if (action.selectedSessionComments.map(sc => sc.fixedCommentId).includes(c.id)) {
                        return {
                            ...c,
                            selected: true,
                        }
                    } else return c;
                }),
            },
            testFixedComments: state.testFixedComments.map(tfc => {
                if (action.selectedTestComments.map(sc => sc.testId).includes(tfc.testId)) {
                    return {
                        ...tfc,
                        comments: tfc.comments.map(c => {
                            if (action.selectedTestComments.filter(t => t.testId === tfc.testId).flatMap(stc => stc.fixedComments.map(f => f.fixedCommentId)).includes(c.id)) {

                                const analysisSetComment = action.selectedTestComments.filter(t => t.testId === tfc.testId).flatMap(stc => stc.fixedComments).find(x => x.fixedCommentId === c.id);
                                return {
                                    ...c,
                                    selected: true,
                                    followOnComment: analysisSetComment.followOnComment
                                }
                            } else return c;
                        }),
                    }
                } else return tfc;
            })
        }

        return {
            ...newState,
            areFixedCommentsLoaded:
                newState.sessionFixedComments.comments
                    .length > 0
                && newState.testFixedComments.map(x => x.comments)
                    .flat()
                    .length > 0,
        }
    }

    const reducer = (state: NextGenFixedCommentsState, action: Actions): NextGenFixedCommentsState => {
        switch (action.type) {
            case NextGenFixedCommentsActionTypes.LoadSessionFixedComments:
                return {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        comments: action.comments.map(c => ({
                            ...c,
                            selected: false,
                        })),
                    },
                };
            case NextGenFixedCommentsActionTypes.UpdateFixedComments:
                const newTestFixedComments = action.updatedAnalysisSet.tests.filter(u => !analysisSet.tests.map(t => t.id).includes(u.id)).map(tr => ({
                    testId: tr.testId,
                    isVisible: false,
                    comments: action.comments.map(c => ({
                        ...c,
                        selected: false,
                    })),
                }));
                return {
                    ...state,
                    testFixedComments: [...state.testFixedComments, ...newTestFixedComments]
                };
            case NextGenFixedCommentsActionTypes.LoadTestFixedComments:
                if (analysisSet === undefined) {
                    return state;
                }

                const fixedComments = analysisSet.tests.map(tr => ({
                    testId: tr.testId,
                    isVisible: false,
                    comments: action.comments.map(c => ({
                        ...c,
                        selected: false,
                    })),
                }));

                return {
                    ...state,
                    testFixedComments: fixedComments,
                };
            case NextGenFixedCommentsActionTypes.LoadedBothAvailableFixedComments:
                return {
                    ...state,
                    areAvailableFixedCommentsLoaded: true,
                };
            case NextGenFixedCommentsActionTypes.ToggleTestFixedCommentsVisibility:
                const existingTest = state.testFixedComments.find(t => t.testId === action.testId);

                const updatedTestFixedComments = existingTest
                    ? state.testFixedComments.map(t => {
                        if (t.testId === action.testId) {
                            return {
                                ...t,
                                isVisible: !t.isVisible,
                            };
                        } else {
                            return t;
                        }
                    })
                    : [...state.testFixedComments, { testId: action.testId, isVisible: true, comments: overreadSelectableComments }];

                return {
                    ...state,
                    testFixedComments: updatedTestFixedComments,
                };
            case NextGenFixedCommentsActionTypes.ToggleSessionFixedCommentChecked:
                return {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        comments: state.sessionFixedComments.comments.map(c => {
                            if (c.id === action.id) {
                                return {
                                    ...c,
                                    selected: action.checked,
                                };
                            } else {
                                return c;
                            }
                        })
                    }
                };
            case NextGenFixedCommentsActionTypes.ToggleTestFixedCommentChecked:
                return {
                    ...state,
                    testFixedComments: state.testFixedComments.map(tfc => {
                        if (tfc.testId === action.testId) {
                            return {
                                ...tfc,
                                comments: tfc.comments.map(c => {
                                    if (c.id === action.commentId) {
                                        return {
                                            ...c,
                                            selected: action.checked,
                                        };
                                    } else {
                                        return c;
                                    }
                                })
                            }
                        } else {
                            return tfc;
                        }
                    }),
                };
            case NextGenFixedCommentsActionTypes.ToggleSessionFixedCommentsVisibility:
                return {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        isVisible: !state.sessionFixedComments.isVisible,
                    },
                };
            case NextGenFixedCommentsActionTypes.LoadAnalysisSetSelections:
                return loadAnalysisSetSelections(state, action);
            default:
                throw new Error("Unexpected Fixed Comments action");

        }
    }

    const [state, dispatch] = React.useReducer(reducer, initialFixedCommentsState);

    React.useEffect(() => {
        const fetchComments = async () => {
            if (sessionFixedComments) {
                dispatch(LoadSessionFixedCommentsAction.create(sessionFixedComments.filter(sfc => !sfc.disabled)));
            }

            if (testFixedComments) {
                dispatch(LoadTestFixedCommentsAction.create(testFixedComments.filter(tfc => !tfc.disabled)));
            }

            dispatch(LoadedBothAvailableFixedCommentsAction.create());
        }

        fetchComments();
    }, [study.id, dispatch, analysisSet, sessionFixedComments, testFixedComments]);

    return [state, dispatch];
}
